import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { v4 } from 'uuid';
import { RequestParameters } from '~/backend/backend-client';
import { Modality } from '~/domain/dicom/modality';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { dosePredictionTaskState } from '~/state/atoms/dose/dosePredictionTaskAtom';
import { OptimizationObjective } from '~/domain/patient/optimizationObjective';
import { environmentState } from '~/state/atoms/engineAtoms';
import { primaryImageState } from '~/state/atoms/image/imageAtoms';
import { Job } from '~/state/atoms/jobAtoms';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import {
    WorkflowTypes,
    pseudonymizeState,
    workflowModalState,
} from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import {
    getHighestDoseQuantity,
    transformDoseTargets,
} from '~/utils/transforms/doseTargetsTransforms';
import { splitDicomAndJsonFiles } from '~/utils/io/files/FileHandler';
import {
    extractOptimizationObjectivesFile,
    readOptimizationObjectives,
} from '~/utils/transforms/optimizationObjectivesTransforms';
import { transformTaskToGenerationModel } from '~/utils/transforms/taskTransforms';
import useProcessDicomFiles from '../../../useProcessDicomFiles';
import useViewportEnvironment from '../../../useViewportEnvironment';
import { NewJobData, useJobManagement } from '../../useJobManagement';
import useAnonymizer from '../useAnonymizer';
import { useDoseBackend, useSessionID } from '../useBackend';
import { optimizationObjectivesState } from '~/state/atoms/dose/optimizationObjectivesAtom';

/**
 * A React hook for creating a dose prediction task.
 * @returns {Object} - An object containing startProcessing function and submission state.
 */
export function useDosePrediction() {
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const primaryImage = useRecoilValue(primaryImageState);
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const environment = useRecoilValue(environmentState);
    const pseudonymize = useRecoilValue(pseudonymizeState);
    const setWorkflowModalState = useSetRecoilState(workflowModalState);
    const dosePredictionTask = useRecoilValue(dosePredictionTaskState);
    const session = useSessionOrNull();
    const predictObjectives = true; // hardcoded to as agreed for release, can be made dynamic in the future if needed
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    const doseClient = useDoseBackend();
    const { addJob, updateJob } = useJobManagement();
    const { anonymizeBlobs, reidentifyFiles } = useAnonymizer();
    const { resetSessionID, sessionID } = useSessionID();
    const { processCreatedFiles } = useProcessDicomFiles();
    const { setEnvironmentDose } = useViewportEnvironment();
    const setOptimizationObjectives = useSetRecoilState(
        optimizationObjectivesState
    );
    const setDosePredictionTask = useSetRecoilState(dosePredictionTaskState);

    const handleProcessedFiles = async (job: Job) => {
        if (job.results && job.results?.length > 0) {
            // Split non-DICOM JSON files from DICOM files up front so the
            // anonymizer/re-identifier never receives JSON bytes. Any JSON
            // file the worker returns (optimization objectives, metadata, etc.)
            // would cause reidentifyFiles to throw and return null, silently
            // dropping the entire dose result.
            const { dicomFiles: dicomResults, jsonFiles } =
                splitDicomAndJsonFiles(job.results);
            const objFile = extractOptimizationObjectivesFile(jsonFiles);

            const finalDicomFiles = job.payload?.pseudonymized
                ? await reidentifyFiles(dicomResults)
                : dicomResults;
            if (finalDicomFiles) {
                let objectives: OptimizationObjective[] = [];
                if (objFile) {
                    try {
                        objectives = await readOptimizationObjectives(objFile);
                    } catch (e) {
                        handleError(
                            e,
                            'Failed to parse optimization objectives.'
                        );
                    }
                }

                const generationModel = transformTaskToGenerationModel(
                    job.task
                );
                const processedItems = await processCreatedFiles(
                    finalDicomFiles,
                    generationModel
                );
                if (processedItems) {
                    if (objectives.length > 0) {
                        processedItems.doses.forEach((dose) => {
                            dose.optimizationObjectives = objectives;
                        });
                    }
                    updateJob(job.id, { isImported: true });
                    const taskTargets =
                        job.task && 'targets' in job.task
                            ? job.task.targets
                            : undefined;
                    if (taskTargets) {
                        const highestPrescription =
                            getHighestDoseQuantity(taskTargets);
                        for (const dose of processedItems.doses) {
                            dose.targets = taskTargets;
                            if (highestPrescription) {
                                dose.normalizationValueGy = highestPrescription;
                            }
                        }
                    }

                    setEnvironmentDose(
                        processedItems.doses,
                        selectedPatientId ?? ''
                    );
                }
            } else {
                handleError('Re-identification failed for one or more files.');
            }
        }
    };

    const startProcessing = async (modelName: string) => {
        if (isSubmitting) {
            return;
        }

        if (primaryImage && currentStructureSet && environment) {
            const modality = primaryImage.seriesMetadata.modality;
            const isValidModality =
                modality === Modality.CT || modality === Modality.MR;
            const isTilted = primaryImage.firstSlice.tilted;
            const isHfs = primaryImage.firstSlice.patientPosition === 'HFS';
            if (!isValidModality || isTilted || !isHfs) {
                const reasons: string[] = [];
                if (!isValidModality) {
                    reasons.push(
                        `unsupported modality ${modality} (expected CT or MR)`
                    );
                }
                if (isTilted) {
                    reasons.push('gantry tilt is present');
                }
                if (!isHfs) {
                    reasons.push('patient position is not HFS');
                }
                const errorMessage = `The selected image is not supported: ${reasons.join(', ')}.`;
                handleError(new Error(errorMessage), errorMessage);
                return;
            }

            setIsSubmitting(true);
            try {
                resetSessionID();
                const taskUID = `${sessionID}.${v4().split('-')[0]}`;

                const blobs = Array.from(primaryImage.getSlicesAsDicomBlobs());
                environment.saveChanges();

                if (!session) {
                    throw new Error(
                        'No active patient session for dose prediction.'
                    );
                }
                blobs.push(
                    session.structureSetExportService.exportBlob(
                        currentStructureSet.dicomUid
                    )
                );
                const blobsToUpload = pseudonymize
                    ? await anonymizeBlobs(taskUID, blobs)
                    : blobs;

                const backendParameters: RequestParameters = {
                    action: modelName,
                    taskUID,
                    nExpectedFiles: blobs.length.toString(),
                };

                if (
                    dosePredictionTask?.targets &&
                    dosePredictionTask.targets.length > 0
                ) {
                    backendParameters.targets = JSON.stringify({
                        unit: 'Gy',
                        rois: transformDoseTargets(dosePredictionTask.targets),
                    });
                }

                backendParameters.predict_objectives =
                    String(predictObjectives);

                if (blobsToUpload) {
                    const newJobDetails: NewJobData = {
                        id: sessionID,
                        name: 'Dose+',
                        totalItems: blobsToUpload.length,
                        type: WorkflowTypes.DOSE,
                        client: doseClient,
                        onLoad: handleProcessedFiles,
                        payload: {
                            filesToUpload: blobsToUpload.map((file) => ({
                                file,
                            })),
                            backendParameters,
                            pseudonymized: pseudonymize,
                        },
                        task: {
                            model: dosePredictionTask.model,
                            targets: dosePredictionTask.targets,
                        },
                        inputApprovalStatus: currentStructureSet.approvalStatus,
                    };

                    addJob(newJobDetails);
                    setWorkflowModalState((state) => ({
                        ...state,
                        open: false,
                    }));
                    navigate('/predictive-planning');
                } else {
                    handleError('Anonymization failed for one or more files.');
                }
            } catch (error) {
                handleError(error);
                setDosePredictionTask((prev) => ({
                    ...prev,
                    targets: undefined,
                    model: undefined,
                }));
                setOptimizationObjectives([]);
            } finally {
                setIsSubmitting(false);
            }
        } else {
            handleError('Image or structure set is missing!');
        }
    };

    return { startProcessing, isSubmitting };
}
